import type { StatusKey } from "../components/Status";

/* ══ 이슈 보드가 읽는 문 — 전부 이미 있는 API 다(M5) ══
   `GET /tasks`·`GET /tasks/:id`·`POST /tasks/:id/status`·`POST /tasks/:id/queue-order`
   (`crates/api/src/tasks.rs`). 여기서 새 API 를 만들지 않는다 — 배선만 한다.

   목록과 상세는 **같은 모양**이라(`task_json`) 상세 팝업은 따로 GET 하지 않고
   목록 응답의 값을 그대로 쓴다. */

export const API = import.meta.env.VITE_B3XIN_API_URL ?? "/api";

export type Priority = "critical" | "high" | "normal";
export type Complexity = "deep" | "hard" | "normal" | "light";
/** `tidy` 는 정리 실행의 앵커 이슈다(`docs/decisions/tidy.md`). `ops` 는 서버 runner 가 집는
 *  서버 제어다(`docs/decisions/ops-task.md`) — 대상(`ops` 칸)을 그리는 화면은 시안 뒤에 선다. */
export type IssueType = "design" | "feature" | "bug" | "chore" | "plan" | "wizard" | "tidy" | "ops";

/** `GET /tasks` 의 한 줄이자 `GET /tasks/:id` 그대로(`task_json`). */
export type Issue = {
  id: string;
  title: string;
  status: StatusKey;
  /** null 은 **미분류**다 — 기본값으로 메우지 않는다. */
  type: IssueType | null;
  priority: Priority;
  complexity: Complexity;
  milestoneId: string | null;
  parentId: string | null;
  /** 에픽 표식용. 부모가 아니면 0 이다. */
  childCount: number;
  doneChildCount: number;
  /** 「◆ 나루 할당」의 판정 기준은 **assigneeKind 가 'held'** 다 — 담당 이름이 아니다. */
  assignee: string | null;
  assigneeKind: "held" | "ticket" | null;
  /** 「⧉ 병합 대기」의 판정 기준은 **status 가 review 이고 prLink 가 있음**이다. */
  prLink: { url: string; createdAt: string } | null;
  /** **null 이 있다**(줄에 아직 안 선 이슈) — 화면이 계산해 채우지 않는다. */
  queueRank: number | null;
  updatedAt: string;
  /** 「도는 중」. **상태가 아니라 열린 세션이다** — 없으면 null. `id` 는 「세션 멈춤」이 쓴다. */
  openSession: { id: string; runner: string; startedAt: string } | null;
  /** 자동 실행 표(「에이전트에게 넘기기」) — 안 세웠으면 null. */
  agentRequestedAt: string | null;
  intent: string;
  acceptance: string[];
  dependsOn: string[];
  /** 소속 프로젝트 — 「하위 추가」가 새 하위에 그대로 적는다(서버는 상위에서 안 물려준다). */
  projectId?: string | null;
  /** 막힌 이슈가 **어느 단계에서** 막혔나와 그 사유(마지막 `→ blocked` 줄). 막힘이 아니면 null.
   *  「리뷰 다시 돌리기」가 서는 조건이고, 판정은 서버(`review-request`)가 다시 한다. */
  blockedFrom?: StatusKey | null;
  blockedNote?: string | null;
  /** 열린 질문 — 상세가 답하는 자리. 답한 것은 안 실린다. */
  openQuestions?: OpenQuestion[];
  /** 안 집어 간 말의 수(우편함). 사실이지 판정이 아니다. */
  pendingMessages?: number;
  /** 서버 제어 대상(`type=ops` 일 때만, 아니면 null). 서버 화면이 「리포 추가」 이슈를 고를 때 읽는다. */
  ops?: OpsSpec | null;
  /** 인계 노트 — 세션이 제출 때 남긴 말(상세 「인계」 탭). 안 남겼으면 null. */
  handoff?: string | null;
  /** 첨부 **목록만**(본문은 `GET /attachments/:id`). 상세 「정보」 탭이 그린다. */
  attachments?: Attachment[];
  createdAt?: string;
  /** 완료 시각 — done 일 때만, 모르면 null(「시각 모름」). 관측 › 이력이 읽는다. */
  doneAt?: string | null;
};

/** `task_json` 의 첨부 한 줄(`TASK_SELECT` 의 `attachments`). */
export type Attachment = { id: string; filename: string; mime: string; size: number; createdBy: string };

/** `GET /tasks/:id/progress` 의 한 줄 — 새것이 먼저다. 처음 만든 줄은 `from` 이 null 이다. */
export type ProgressRow = { from: StatusKey | null; to: StatusKey; actor: string; note: string | null; at: string };

/** `task_question` 의 열린 한 줄(`TASK_SELECT` 의 `open_questions`). */
export type OpenQuestion = { id: string; prompt: string; options: string[] | null; askedStatus: StatusKey; createdAt: string };

/** `POST /tasks/:id/children/ready` 의 답 — 전부-아니면-전무가 아니다. */
export type ChildrenReadyResult = {
  moved: string[];
  skipped: { id: string; reason: string | null; message?: string | null; status: StatusKey }[];
  openDeps: { id: string; blockedBy: string[] }[];
};

/** `PATCH /tasks/:id` 가 받는 스펙 칸(상태는 안 받는다). 빈 문자열 = 비운다(유형 = 미분류). */
export type SpecPatch = Partial<{
  title: string;
  intent: string;
  acceptance: string[];
  type: IssueType | "";
  priority: Priority;
  complexity: Complexity;
  dependsOn: string[];
}>;

/** `task.ops` — `domain::ops_spec_error` 가 모양을 정한다. */
export type OpsSpec = {
  host: string;
  cmd: string;
  service?: string;
  pull?: boolean;
  repo?: string;
  branch?: string;
  port?: string;
};

/** `GET /milestones` 의 한 줄(`crates/api/src/milestones.rs`). 보드는 id·title 만 쓴다.
 *  `doneCount` 는 **완료만** 센다(취소는 「해냈나」가 아니다) — 마일스톤 화면의 진행률이 이 값 그대로다. */
export type Milestone = {
  id: string;
  title: string;
  orderRank: number | null;
  taskCount: number;
  doneCount: number;
  projectId?: string | null;
  goal?: string | null;
};

class HttpError extends Error {
  constructor(
    public status: number,
    public path: string,
    msg: string,
  ) {
    super(msg);
  }
}

/** 실패는 셋을 말한다 — 무엇이(HTTP) · 어디서(주소) · 서버의 문장(`message`, 없으면 `error`). */
export async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${API}${path}`, init);
  // 204(지우기)는 본문이 없다 — 읽으면 JSON 오류가 실패로 둔갑한다.
  if (r.status === 204) return undefined as T;
  if (r.ok) return (await r.json()) as T;
  let msg = `HTTP ${r.status}`;
  try {
    const j = (await r.json()) as { message?: string; error?: string };
    if (j.message) msg = `${j.message} (HTTP ${r.status})`;
    else if (j.error) msg = `${j.error} (HTTP ${r.status})`;
  } catch {
    // 본문이 JSON 이 아니면 상태 코드만 말한다.
  }
  throw new HttpError(r.status, `${API}${path}`, msg);
}

const post = (path: string, body?: unknown) =>
  call<Issue>(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });

export const tasksApi = {
  list: () => call<Issue[]>(`/tasks`),
  milestones: () => call<Milestone[]>(`/milestones`),
  /** 새 마일스톤 — `id` 를 비우면 서버가 만든다. 같은 id 는 409. 줄의 맨 뒤에 선다(서버가 정한다). */
  createMilestone: (b: { id?: string; title: string; goal?: string; projectId?: string }) =>
    call<Milestone>(`/milestones`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(b) }),
  /** 이슈를 마일스톤에 넣는다 — `null` 이면 뺀다(서버는 빈 문자열을 「뺀다」로 읽는다). */
  setMilestone: (id: string, milestoneId: string | null) =>
    call<Issue>(`/tasks/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ milestoneId: milestoneId ?? "" }),
    }),
  /** 열 사이로 옮기기 — 상태 전이. 서버가 거절하면 그 문장을 그대로 던진다
   *  (전이표를 화면에 두지 않는다). */
  setStatus: (id: string, to: StatusKey) => post(`/tasks/${encodeURIComponent(id)}/status`, { to }),
  /** 상태 이동 기록(상세 「이력」 탭). */
  progress: (id: string) => call<ProgressRow[]>(`/tasks/${encodeURIComponent(id)}/progress`),
  /** 열 안에서 순서 바꾸기 — **이웃 두 id 만** 보낸다. 순위 값은 서버가 정한다. */
  setQueueOrder: (id: string, prevId?: string, nextId?: string) =>
    post(`/tasks/${encodeURIComponent(id)}/queue-order`, { prevId, nextId }),
};

/** 상세의 조작 줄이 부르는 문 — 전부 서버가 판정하고, 거절은 서버 문장을 그대로 보인다. */
export const issueActsApi = {
  setStatus: tasksApi.setStatus,
  /** 사람이 담당을 걸거나(이름 → 맡아 둠) 푼다(null). `POST /tasks/:id/assignee`. */
  setAssignee: (id: string, assignee: string | null) => post(`/tasks/${encodeURIComponent(id)}/assignee`, { assignee }),
  /** 자동 실행 표 — 세우면 비어 있는 나루가 집는다. */
  agentRequest: (id: string, requested: boolean) =>
    post(`/tasks/${encodeURIComponent(id)}/agent-request`, { requested }),
  stopSession: (sessionId: string) =>
    call<unknown>(`/agent-sessions/${encodeURIComponent(sessionId)}/stop`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: "{}",
    }),
  remove: (id: string, cascade: boolean) =>
    call<unknown>(`/tasks/${encodeURIComponent(id)}${cascade ? "?cascade=true" : ""}`, { method: "DELETE" }),
  prompt: (id: string, track: string) =>
    call<{ track: string; prompt: string }>(`/tasks/${encodeURIComponent(id)}/prompt?track=${encodeURIComponent(track)}`),
  /** 스펙 · 칩을 고친다(`PATCH /tasks/:id`). `dependsOn` 은 **더한다** — 통째 교체가 아니다. */
  patch: (id: string, body: SpecPatch) =>
    call<Issue>(`/tasks/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }),
  removeDep: (id: string, dep: string) =>
    call<unknown>(`/tasks/${encodeURIComponent(id)}/dependencies/${encodeURIComponent(dep)}`, { method: "DELETE" }),
  /** 에픽의 잎 하위 중 할 일 · 막힘을 대기로(자동 실행은 안 켠다). */
  childrenReady: (id: string) =>
    call<ChildrenReadyResult>(`/tasks/${encodeURIComponent(id)}/children/ready`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: "{}",
    }),
  /** 막힘 보완 — 말을 우편함에 남기고 대기로 올려 자동 실행을 켠다(사람만). */
  supplement: (id: string, note: string) => post(`/tasks/${encodeURIComponent(id)}/supplement`, { note }),
  /** 리뷰에서 막힌 이슈를 리뷰로 되돌려 다시 돌린다(사람만). */
  reviewRequest: (id: string) => post(`/tasks/${encodeURIComponent(id)}/review-request`),
  answer: (id: string, qid: string, answer: string) =>
    post(`/tasks/${encodeURIComponent(id)}/questions/${encodeURIComponent(qid)}/answer`, { answer }),
  /** 하위 이슈 — 프로젝트는 상위에서 안 물려받으니 부르는 쪽이 넘긴다. */
  createChild: (parentId: string, title: string, projectId?: string | null) =>
    call<Issue>(`/tasks`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ title, parentId, ...(projectId ? { projectId } : {}) }),
    }),
};

/** 스펙이 비었나 — `track::track_of` 가 대기 이슈를 **기획**으로 읽는 조건과 같다(의도 · 수용 기준).
 *  「기획 배차」 단추가 서는 자리만 고른다 — 어느 트랙으로 갈지는 서버가 정한다. */
export const needsSpec = (t: Pick<Issue, "intent" | "acceptance">) => !t.intent.trim() || t.acceptance.length === 0;
export type IssueActsApi = typeof issueActsApi;

/** 나루에게 맡긴다 — 할 일은 트랙이 없어서(`track::track_of`) 대기로 먼저 올리고, 걸린 담당은
 *  나루를 막으니(사람이 맡아 둔 것은 기계가 지나가지 않는다) 먼저 푼다. 상세 · 보드 선택 줄이
 *  같은 이 함수를 부른다 — 순서를 두 곳에 적지 않는다. */
export async function handToNaru(api: IssueActsApi, t: Pick<Issue, "id" | "status" | "assignee">) {
  if (t.status === "todo") await api.setStatus(t.id, "ready");
  if (t.assignee) await api.setAssignee(t.id, null);
  await api.agentRequest(t.id, true);
}

export function errText(e: unknown): string {
  if (e instanceof HttpError) return `${e.message} — ${e.path}`;
  if (e instanceof Error) return e.message;
  return String(e);
}

/** 판정 셋 — **한 곳**에서만 한다(`docs/screens/issue-screens-data.md`). 화면은 다시
 *  판정하지 않고 이 셋만 부른다. */
export const isHeld = (t: Pick<Issue, "assigneeKind">) => t.assigneeKind === "held";
export const isMerging = (t: Pick<Issue, "status" | "prLink">) => t.status === "review" && t.prLink !== null;
export const isWorking = (t: Pick<Issue, "openSession">) => t.openSession !== null;
